import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { QRCodeSVG } from "qrcode.react";
import { trpc } from "@/lib/trpc";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { InlineConfirmButton } from "@/components/shared/InlineConfirmButton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Loader2,
  Plus,
  QrCode,
  Info,
  Settings,
  Copy,
  ChevronDown,
  ChevronUp,
  Trash2,
  UserPlus,
  Clock,
  Home,
  Briefcase,
} from "lucide-react";
import { useCommunity } from "@/contexts/CommunityContext";
import { toast } from "sonner";

const categoryLabels = {
  TEMPORARY: "Temporal",
  PERMANENT: "Permanente",
  SERVICE: "Proveedor",
};

const categoryIcons = {
  TEMPORARY: Clock,
  PERMANENT: Home,
  SERVICE: Briefcase,
};

const statusColors = {
  PENDING: "secondary",
  ACTIVE: "default",
  REVOKED: "destructive",
  EXPIRED: "outline",
} as const;

const statusBorderColors: Record<string, string> = {
  PENDING: "border-l-amber-500",
  ACTIVE: "border-l-green-500",
  REVOKED: "border-l-red-500",
  EXPIRED: "border-l-gray-400",
};

function formatDate(date: Date | string) {
  return new Date(date).toLocaleDateString("es-AR", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

type InvitationCategory = "TEMPORARY" | "PERMANENT" | "SERVICE";

export function InvitationsPage() {
  const utils = trpc.useUtils();
  const { selectedCommunityId } = useCommunity();
  const [activeTab, setActiveTab] = useState<InvitationCategory>("TEMPORARY");
  const [expandedInvitation, setExpandedInvitation] = useState<string | null>(
    null,
  );

  const { data: communities, isLoading: communitiesLoading } =
    trpc.amenity.getUserCommunities.useQuery();

  const { data: adminCommunities } =
    trpc.amenity.getAdminCommunities.useQuery();
  const hasAdminAccess =
    selectedCommunityId &&
    adminCommunities?.some((c) => c.id === selectedCommunityId);

  const { data: units } = trpc.invitation.getUserUnits.useQuery(
    { communityId: selectedCommunityId || "" },
    { enabled: !!selectedCommunityId },
  );

  const { data: invitations, isLoading: invitationsLoading } =
    trpc.invitation.getMyInvitations.useQuery(
      { communityId: selectedCommunityId || "", category: activeTab },
      { enabled: !!selectedCommunityId },
    );

  const revokeInvitation = trpc.invitation.revoke.useMutation({
    onSuccess: () => {
      utils.invitation.getMyInvitations.invalidate();
      toast.success("Invitación revocada");
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const copyInviteLink = (token: string) => {
    const inviteUrl = `${window.location.origin}/invite/${token}`;
    navigator.clipboard.writeText(inviteUrl);
    toast.success("Enlace copiado al portapapeles");
  };

  // No communities
  if (!communitiesLoading && (!communities || communities.length === 0)) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-6 md:px-6 md:py-10">
        <div className="mb-8">
          <h1 className="text-2xl font-bold md:text-3xl">Invitaciones</h1>
          <p className="text-muted-foreground">
            Creá y gestioná invitaciones para tus invitados.
          </p>
        </div>

        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            No sos miembro de ningún barrio. Contactá al administrador para
            obtener acceso.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // No units assigned
  if (!communitiesLoading && units && units.length === 0) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-6 md:px-6 md:py-10">
        <div className="mb-8">
          <h1 className="text-2xl font-bold md:text-3xl">Invitaciones</h1>
          <p className="text-muted-foreground">
            Creá y gestioná invitaciones para tus invitados.
          </p>
        </div>

        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            No tenés ninguna unidad asignada. Contactá al administrador de tu
            barrio.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-4xl px-4 py-6 md:px-6 md:py-10">
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold md:text-3xl">Invitaciones</h1>
          <p className="text-muted-foreground">
            Creá y gestioná invitaciones para tus invitados.
          </p>
        </div>

        <div className="flex items-center gap-2">
          {hasAdminAccess && (
            <Link
              to="/invitations/manage"
              className="rounded-md p-2 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
            >
              <Settings className="h-5 w-5" />
            </Link>
          )}

          <Button asChild>
            <Link to="/invitations/create">
              <Plus className="mr-2 h-4 w-4" />
              Nueva invitación
            </Link>
          </Button>
        </div>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(v) => setActiveTab(v as InvitationCategory)}
      >
        <TabsList className="mb-6">
          <TabsTrigger value="TEMPORARY">
            <Clock className="mr-2 h-4 w-4" />
            Temporal
          </TabsTrigger>
          <TabsTrigger value="PERMANENT">
            <Home className="mr-2 h-4 w-4" />
            Permanente
          </TabsTrigger>
          <TabsTrigger value="SERVICE">
            <Briefcase className="mr-2 h-4 w-4" />
            Proveedor
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab}>
          {communitiesLoading || invitationsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : invitations && invitations.length > 0 ? (
            <div className="space-y-4">
              {invitations.map((invitation) => {
                const CategoryIcon = categoryIcons[invitation.category];
                const isExpanded = expandedInvitation === invitation.id;

                return (
                  <Card
                    key={invitation.id}
                    className={`border-l-4 ${statusBorderColors[invitation.status] || ""}`}
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-muted">
                            {invitation.status === "PENDING" ? (
                              <UserPlus className="h-5 w-5" />
                            ) : (
                              <CategoryIcon className="h-5 w-5" />
                            )}
                          </div>
                          <div>
                            <CardTitle className="text-lg">
                              {invitation.guestName ||
                                "Pendiente de datos del invitado"}
                            </CardTitle>
                            <CardDescription className="flex flex-wrap items-center gap-2">
                              <Badge
                                variant={statusColors[invitation.status]}
                                className="text-xs"
                              >
                                {invitation.status.toLowerCase()}
                              </Badge>
                              <span className="text-xs">
                                {invitation.unit.blockSection
                                  ? `${invitation.unit.blockSection} - ${invitation.unit.unitNumber}`
                                  : invitation.unit.unitNumber}
                              </span>
                            </CardDescription>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label="Copiar enlace de invitación"
                            onClick={() => copyInviteLink(invitation.token)}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>

                          {invitation.status !== "REVOKED" && (
                            <InlineConfirmButton
                              onConfirm={() =>
                                revokeInvitation.mutate({
                                  invitationId: invitation.id,
                                })
                              }
                              isPending={
                                revokeInvitation.isPending &&
                                revokeInvitation.variables?.invitationId ===
                                  invitation.id
                              }
                              confirmLabel="Revocar"
                              aria-label="Revocar invitación"
                            >
                              <Trash2 className="h-4 w-4" />
                            </InlineConfirmButton>
                          )}

                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label={
                              isExpanded
                                ? "Contraer detalles"
                                : "Expandir detalles"
                            }
                            onClick={() =>
                              setExpandedInvitation(
                                isExpanded ? null : invitation.id,
                              )
                            }
                          >
                            {isExpanded ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </div>
                    </CardHeader>

                    <CardContent className="pt-0">
                      <div className="text-sm text-muted-foreground">
                        {invitation.category === "PERMANENT" ? (
                          <span>
                            Válida desde {formatDate(invitation.validFrom)}
                          </span>
                        ) : (
                          <span>
                            {formatDate(invitation.validFrom)} -{" "}
                            {invitation.validUntil
                              ? formatDate(invitation.validUntil)
                              : "Sin vencimiento"}
                          </span>
                        )}
                      </div>

                      {isExpanded && (
                        <div className="mt-4 space-y-4">
                          {invitation.guestEmail && (
                            <div className="text-sm">
                              <span className="text-muted-foreground">
                                Email:{" "}
                              </span>
                              {invitation.guestEmail}
                            </div>
                          )}
                          {invitation.guestDni && (
                            <div className="text-sm">
                              <span className="text-muted-foreground">
                                DNI:{" "}
                              </span>
                              {invitation.guestDni}
                            </div>
                          )}
                          {invitation.notes && (
                            <div className="text-sm">
                              <span className="text-muted-foreground">
                                Notas:{" "}
                              </span>
                              {invitation.notes}
                            </div>
                          )}

                          {invitation.status === "ACTIVE" && (
                            <div className="flex flex-col items-center gap-2 rounded-xl border bg-white p-4">
                              <QRCodeSVG
                                value={`${window.location.origin}/invite/${invitation.token}`}
                                size={200}
                                level="M"
                                includeMargin
                              />
                              <p className="text-sm text-muted-foreground">
                                Escaneá para ver la invitación
                              </p>
                            </div>
                          )}

                          {invitation.status === "PENDING" && (
                            <Alert>
                              <Info className="h-4 w-4" />
                              <AlertDescription>
                                Compartí el enlace con tu invitado para que
                                complete sus datos.
                              </AlertDescription>
                            </Alert>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <QrCode className="mx-auto h-12 w-12 text-muted-foreground" />
                <p className="mt-4 text-muted-foreground">
                  No hay invitaciones de tipo{" "}
                  {categoryLabels[activeTab].toLowerCase()} todavía.
                </p>
                <Button variant="outline" className="mt-4" asChild>
                  <Link to="/invitations/create">
                    <Plus className="mr-2 h-4 w-4" />
                    Creá tu primera invitación
                  </Link>
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
